import React, { FC, useEffect } from 'react';
import { TypePage } from '@/core/generated-types';
import { Seo } from '@/components/ui/Seo';
import { useYotpoScript } from '@/hooks/useYotpoScript';
import { buildCanonicalUrl } from '@/core/contentfulConfig';
import { TypeUserRecord } from '@/lib/api/accountManager/types';
import { useRouter } from 'next/router';
import { useRouter as useLocaleRouter } from '@/navigation';
import { DynamicComponent } from '@/components/common';
import useLocale from '@/hooks/useLocale';

export interface ReferralLayoutProps {
  page: TypePage;
  user: TypeUserRecord;
}

export const META_IMAGE_URL: Record<string, string> = Object.freeze({
  'en-US': 'share-image-en-us.jpg',
  'en-AU': 'share-image.jpg',
  es: 'share-image-es.jpg',
  de: 'share-image-de.de',
  nl: 'share-image-nl.jpg',
  fr: 'share-image-fr.jpg',
  'en-EU': 'share-image.jpg',
  'en-GB': 'share-image.jpg',
});

export const ReferralLayout: FC<ReferralLayoutProps> = ({ page, user }) => {
  const router = useRouter();
  const localeRouter = useLocaleRouter();
  const locale = useLocale();
  const isShareLink = !!router.query?.discount_code;

  useEffect(() => {
    if (isShareLink) {
      const qs = new URLSearchParams(router.query as Record<string, string>);
      let url = '/products/the-play-kits';
      url = url + `?${qs.toString()}`;
      localeRouter.replace(url);
    }
  }, [router, isShareLink, localeRouter]);

  const {
    fields: { title, components, longDescription },
  } = page;
  const metadataFields = page.fields?.metadata?.fields;

  useYotpoScript();

  return (
    <>
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        canonical={buildCanonicalUrl(page)}
        twitter={{
          cardType: 'summary_large_image',
          site: '@lovevery',
          handle: '@lovevery',
        }}
        openGraph={{
          url: buildCanonicalUrl(page),
          images: [
            {
              url: `http://${process.env.NEXT_PUBLIC_VANITY_DOMAIN}/assets/images/${META_IMAGE_URL[locale]}`,
              secureUrl: `https://${process.env.NEXT_PUBLIC_VANITY_DOMAIN}/assets/images/${META_IMAGE_URL[locale]}`,
              width: 1200,
              height: 630,
              alt: 'Lovevery',
            },
          ],
        }}
      />
      {!isShareLink && (
        <>
          <div className="overflow-hidden">
            {title && (
              <h1 className="container mx-auto mt-8 text-center text-h-x3 font-bold">
                {title}
              </h1>
            )}
            {longDescription && (
              <p className="container mx-auto">{longDescription}</p>
            )}
            <div>
              {components &&
                components.map((component, i) => (
                  <DynamicComponent
                    key={`${component.sys.id}-${i}`}
                    component={component}
                    user={user}
                  />
                ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
